import * as THREE from "three";
import type { Scenery } from "../../prims/scenery";
import { toPoints } from "./coaster";
import type { Kit } from "./kit";

// The theme park's small things (#156): a circus tent, lamp posts, string
// lights, fences, benches, pool umbrellas, a balloon cart, trees, paths and
// plain buildings for behind the walls. Each faces +u where it has a front.

const BULBS = ["#ffd166", "#ef476f", "#06d6a0", "#4cc9f0", "#f78c6b"];
const BALLOONS = ["#ef476f", "#ffd166", "#06d6a0", "#118ab2", "#9b5de5", "#f78c6b", "#e63946"];

/** A big top: striped walls, a striped roof up to a pole and a pennant, and a flap open at the front. `len` across. */
export function tent(kit: Kit, s: Scenery): THREE.Object3D {
  const root = new THREE.Group();
  const r = (s.len ?? 10) / 2;
  const stripe = s.color ?? "#e63946";
  kit.stripedDrum(root, r, 2.6, 0, 20, [stripe, "#f4f1ea"]);
  kit.stripedCone(root, r + 0.3, r * 0.7, 2.6, 20, [stripe, "#f4f1ea"]);
  kit.box(root, 0.1, 2.2, 1.8, "#3d2c2e", r + 0.02, 0, 0, "cloth");
  const top = 2.6 + r * 0.7;
  kit.rod(root, new THREE.Vector3(0, top - 0.2, 0), new THREE.Vector3(0, top + 1.4, 0), 0.06, "#ffd166");
  kit.box(root, 0.04, 0.4, 0.9, "#ffd166", 0, top + 0.9, 0.45, "cloth");
  return root;
}

/** A park lamp post: a fluted post and two round lamps on a crossbar. */
export function lamp(kit: Kit, s: Scenery): THREE.Object3D {
  const root = new THREE.Group();
  const h = s.h ?? 4;
  const iron = s.color ?? "#2b3a42";
  kit.cyl(root, 0.18, 0.24, 0.5, iron, 0, 0, 0, 8, "metal");
  kit.cyl(root, 0.07, 0.09, h - 0.5, iron, 0, 0.5, 0, 8, "metal");
  kit.box(root, 1.2, 0.06, 0.06, iron, 0, h - 0.4, 0, "metal");
  for (const x of [-0.6, 0.6]) {
    const globe = new THREE.Mesh(kit.geom("globe", () => new THREE.IcosahedronGeometry(0.22, 1)), kit.glow("#fff3cf"));
    globe.position.set(x, h - 0.2, 0);
    root.add(globe);
  }
  return root;
}

/** String lights along a path: a pole at every point (the point's `y` tall, or 4.5 m), bulbs on a sagging wire between. */
export function lights(kit: Kit, s: Scenery): THREE.Object3D {
  const root = new THREE.Group();
  const pts = toPoints(s.path!).map((p) => p.setY(p.y || 4.5));
  for (const p of pts) kit.rod(root, new THREE.Vector3(p.x, 0, p.z), p, 0.07, "#2b3a42");
  let k = 0;
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i]!;
    const b = pts[i + 1]!;
    const n = Math.max(2, Math.round(a.distanceTo(b) / 0.8));
    const sag = Math.min(1, a.distanceTo(b) * 0.06);
    let prev = a;
    for (let j = 1; j <= n; j++) {
      const t = j / n;
      const p = a.clone().lerp(b, t);
      p.y -= sag * 4 * t * (1 - t);
      kit.rod(root, prev, p, 0.012, "#2b3a42", "metal", 3);
      if (j < n) {
        const bulb = new THREE.Mesh(kit.geom("bulb", () => new THREE.IcosahedronGeometry(0.09, 0)), kit.glow(BULBS[k++ % BULBS.length]!));
        bulb.position.copy(p).y -= 0.08;
        root.add(bulb);
      }
      prev = p;
    }
  }
  return root;
}

/** A low railing along a path, posts every metre and a half; each point's `y` sets its height (1.1 m when 0). */
export function fence(kit: Kit, s: Scenery): THREE.Object3D {
  const root = new THREE.Group();
  const color = s.color ?? "#f4f1ea";
  const pts = toPoints(s.path!).map((p) => p.setY(p.y || 1.1));
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i]!;
    const b = pts[i + 1]!;
    const n = Math.max(1, Math.round(a.distanceTo(b) / 1.5));
    for (let j = 0; j <= n; j++) {
      const p = a.clone().lerp(b, j / n);
      kit.box(root, 0.1, p.y, 0.1, color, p.x, 0, p.z, "paintedMetal");
    }
    for (const f of [0.45, 0.95]) kit.rod(root, a.clone().setY(a.y * f), b.clone().setY(b.y * f), 0.035, color, "paintedMetal", 4);
  }
  return root;
}

/** A park bench, 1.8 m long across w, facing +u. */
export function bench(kit: Kit, s: Scenery): THREE.Object3D {
  const root = new THREE.Group();
  const wood = s.color ?? "#9c6b3c";
  for (const z of [-0.8, 0.8]) {
    kit.box(root, 0.5, 0.45, 0.06, "#2b3a42", 0, 0, z, "metal");
    kit.box(root, 0.06, 0.5, 0.06, "#2b3a42", -0.25, 0.45, z, "metal");
  }
  for (const x of [-0.15, 0, 0.15]) kit.box(root, 0.12, 0.04, 1.8, wood, x, 0.45, 0, "plywood");
  for (const y of [0.62, 0.8]) kit.box(root, 0.04, 0.12, 1.8, wood, -0.26, y, 0, "plywood");
  return root;
}

/** A pool umbrella over a sun lounger. */
export function umbrella(kit: Kit, s: Scenery): THREE.Object3D {
  const root = new THREE.Group();
  kit.rod(root, new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 2.4, 0), 0.04, "#f4f1ea");
  kit.stripedCone(root, 1.4, 0.5, 2.2, 8, [s.color ?? "#ef476f", "#f4f1ea"]);
  kit.box(root, 1.8, 0.25, 0.65, "#f4f1ea", 0.4, 0.15, 0.9, "plastic");
  const back = kit.box(root, 0.7, 0.08, 0.65, "#4cc9f0", -0.3, 0.45, 0.9, "cloth");
  back.rotation.z = 0.6;
  kit.box(root, 1.1, 0.06, 0.62, "#4cc9f0", 0.6, 0.4, 0.9, "cloth");
  return root;
}

/** A balloon seller's cart with a bunch of balloons on strings. */
export function balloons(kit: Kit, _s: Scenery): THREE.Object3D {
  const root = new THREE.Group();
  kit.box(root, 1.2, 0.8, 0.7, "#f4f1ea", 0, 0.3, 0);
  for (const z of [-0.4, 0.4]) {
    const wheel = kit.cyl(root, 0.28, 0.28, 0.08, "#2b3a42", -0.3, 0.28 - 0.04, z, 12, "metal");
    wheel.rotation.x = Math.PI / 2;
  }
  const knot = new THREE.Vector3(0.3, 1.1, 0);
  for (let i = 0; i < 9; i++) {
    const a = i * 2.4;
    const top = new THREE.Vector3(0.3 + Math.cos(a) * 0.5 * ((i % 3) / 2 + 0.3), 2.4 + (i % 3) * 0.35, Math.sin(a) * 0.5 * ((i % 3) / 2 + 0.3));
    kit.rod(root, knot, top, 0.008, "#f4f1ea", "plastic", 3);
    const b = kit.ball(root, 0.24, BALLOONS[i % BALLOONS.length]!, top.x, top.y + 0.24, top.z);
    b.scale.y *= 1.2;
  }
  return root;
}

/** A round park tree. */
export function tree(kit: Kit, s: Scenery): THREE.Object3D {
  const root = new THREE.Group();
  const leaf = s.color ?? "#4f9d4a";
  kit.cyl(root, 0.18, 0.26, 2.2, "#7a4f2c", 0, 0, 0, 8, "plywood");
  kit.ball(root, 1.5, leaf, 0, 3.1, 0, "grass");
  kit.ball(root, 1.0, leaf, 0.7, 3.8, 0.3, "grass");
  kit.ball(root, 0.9, leaf, -0.6, 3.6, -0.4, "grass");
  return root;
}

/** A painted path on the ground, `len` along by `wide` across, `h` up (on a road, #167): render only, flat. */
export function paving(kit: Kit, s: Scenery): THREE.Object3D {
  const m = new THREE.Mesh(new THREE.PlaneGeometry(s.len ?? 4, s.wide ?? 2), kit.mat(s.color ?? "#c9a27e", "concrete", { roughness: 0.95, polygonOffset: true, polygonOffsetFactor: -1 }));
  m.rotation.x = -Math.PI / 2;
  m.position.y = (s.h ?? 0) + 0.006;
  m.receiveShadow = true;
  // In a group, so turning the piece about y doesn't tip the flat plane.
  return new THREE.Group().add(m);
}

/** A plain building for behind the walls: `len` along, `wide` deep, `h` tall, rows of windows and a striped awning on its +u face. */
export function building(kit: Kit, s: Scenery): THREE.Object3D {
  const root = new THREE.Group();
  const len = s.len ?? 10;
  const wide = s.wide ?? 6;
  const h = s.h ?? 8;
  const color = s.color ?? "#e0c9a6";
  kit.box(root, wide, h, len, color, 0, 0, 0, "concrete");
  kit.box(root, wide + 0.3, 0.4, len + 0.3, "#f4f1ea", 0, h, 0, "concrete");
  const cols = Math.max(1, Math.floor(len / 2.4));
  for (let row = 1; row * 2.8 < h - 1; row++) {
    for (let c = 0; c < cols; c++) {
      const z = -len / 2 + ((c + 0.5) * len) / cols;
      kit.box(root, 0.08, 1.2, 1.0, "#3d4e63", wide / 2 + 0.02, row * 2.8 - 0.2, z, "glass");
    }
  }
  const stripes = Math.max(4, Math.round(len / 0.8));
  for (let i = 0; i < stripes; i++) {
    const z = -len / 2 + ((i + 0.5) * len) / stripes;
    const a = kit.box(root, 1.4, 0.05, len / stripes, i % 2 ? "#f4f1ea" : "#118ab2", wide / 2 + 0.6, 2.4, z, "cloth");
    a.rotation.z = -0.25;
  }
  // Its shop's name over the awning (#233).
  if (s.sign) kit.sign(root, s.sign, color, Math.min(len - 1, 4.4), wide / 2 + 0.08, 3.4, 0);
  return root;
}
